import React from "react";
import { useSelector } from "react-redux";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { LiaEdit } from "react-icons/lia";
import { RiLink } from "react-icons/ri";
import { AppState } from "../../redux/store";
import { RoleState } from "../../types/models/RoleState";
import { fontColor } from "../../constants/ColorSets";
import { RoleBundleMapping } from "../../types/models/LTOSetup";

const BoxIcon = RiLink as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = LiaEdit as React.FC<React.SVGProps<SVGSVGElement>>;

interface LTOSetupItemProp {
  setup: RoleBundleMapping;
  onEdit: (LTOSetup: RoleBundleMapping) => void;
}

export const LTOSetupItem: React.FC<LTOSetupItemProp> = ({ setup, onEdit }) => {
  const roleState = useSelector<AppState, RoleState>((state) => state.role);

  const getSkillName = (skillId: string) => {
    const skill = roleState.skillList?.find((s) => s.SkillId === skillId);
    return skill ? skillId + " - " + skill.DisplayName : skillId;
  };

  const onClickEditButton = () => {
    onEdit(setup);
  };

  const generateLinkingItems = () => {
    if (setup.Organisation) {
      return (
        <Box
          sx={{
            borderRadius: 1,
            backgroundColor: fontColor.grayBackground,
            py: 0.5,
            px: 1,
            fontSize: 14,
            fontWeight: 500,
            color: fontColor.titleFontColor,
          }}
        >
          {setup.Organisation}
        </Box>
      );
    } else {
      return setup.SkillIds.map((skillId) => (
        <Box
          key={skillId}
          sx={{
            borderRadius: 1,
            backgroundColor: fontColor.grayBackground,
            py: 0.5,
            px: 1,
            fontSize: 14,
            fontWeight: 500,
            color: fontColor.titleFontColor,
          }}
        >
          {getSkillName(skillId)}
        </Box>
      ));
    }
  };

  const generateLinkingSubtitle = () => {
    if (setup.Organisation) {
      return "Organisation";
    } else {
      return setup.SkillIds.length + " skills";
    }
  };

  return (
    <Box>
      <Grid container width="100%" wrap="nowrap" alignItems="stretch">
        <Grid
          item
          sx={{
            width: "60px",
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              mt: 1.5,
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: fontColor.orangeHoverBackground,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BoxIcon fontSize={20} color={fontColor.orangeTitle} />
          </Box>
        </Grid>
        <Grid
          item
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Typography
              fontWeight={600}
              fontSize={18}
              color={fontColor.titleFontColor}
              sx={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {setup.Name}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignContent: "flex-start",
              width: "100%",
              gap: 1,
              mt: 1,
            }}
          >
            {generateLinkingItems()}
          </Box>
        </Grid>
        <Grid
          item
          sx={{
            width: "150px",
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
          }}
        >
          <Typography fontSize={15} color={fontColor.subTitleFontColor} sx={{ mt: 2.2 }}>
            {generateLinkingSubtitle()}
          </Typography>
          <IconButton sx={{ m: 1 }} onClick={() => onClickEditButton()}>
            <EditIcon color={fontColor.subTitleFontColor} />
          </IconButton>
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: "solid lightgrey 1px" }} />
    </Box>
  );
};
